/* eslint-disable no-bitwise */
import { ErrorCatalog, faultsG1, faultsG2, faultsG3, faultsPowerMagic } from './fault.support';
import { AvoidType, DataType, ErrorFeature, InverterContentType, InverterData, InverterDataComposer, InverterRegister, RegisterPage, RegisterTable } from './inverter.model';

/* checksum modbus */
export const crc16 = (buffer: Uint8Array): { lowByte: number; highByte: number } => {
  let crc = 0xffff;
  // eslint-disable-next-line @typescript-eslint/prefer-for-of
  for (let i = 0; i < buffer.length; i += 1) {
    crc ^= buffer[i];
    for (let j = 0; j < 8; j += 1) {
      if ((crc & 0x0001) !== 0) {
        crc >>= 1;
        crc ^= 0xa001;
      } else {
        crc >>= 1;
      }
    }
  }
  return { lowByte: crc & 0xff, highByte: (crc >> 8) & 0xff };
};

export const detectModbusError = (data: Uint8Array): boolean => data[1] >= 0x80;

/** Decode modbus response
 * Is necessary shift 3 bytes because
 * the first 3 bytes are the slaveID, functionType and quantity of registers
 */
export const decodeHoldingRegisters = (
  startAddress: number,
  quantity: number,
  data: Uint8Array,
): { [key: number]: number } => {
  const shift = 3;
  const result: { [key: number]: number } = {};
  for (let i = 0; i < quantity; i++) {
    result[startAddress + i] = data[shift + i * 2] << 8 | data[shift + i * 2 + 1];
  }
  return result;
};

/**
 * Create a sorted array of address from a register table, with register quantity
 */
export const getAddressList = (registers: Partial<RegisterTable>): [number, number][] => (Object.values(registers) as InverterRegister[])
  .sort((a, b) => a.address - b.address)
  .map(
    (register) => [register.address, register.address + register.quantity],
  );

/**
 * Create pages of registers to read
 * Attention: maxPageSize must be larger than the read ranges
 */
export const getMinRegisterPages = (registers: Partial<RegisterTable>, maxPageSize: number, optimize = true): RegisterPage[] => {
  const addressesList = getAddressList(registers);
  const pages: RegisterPage[] = [];
  const [startAddress, lastAddress] = addressesList[0];
  let currentPage: RegisterPage | undefined = {
    address: startAddress,
    quantity: lastAddress - startAddress,
  };
  for (let index = 1; index < addressesList.length; index++) {
    const [start, last] = addressesList[index];
    const avoidRegister = Object.values(registers).find((register) => register?.address === start && register.avoid);
    if (avoidRegister?.avoid === AvoidType.SKIP_REGISTER) {
      if (currentPage) {
        pages.push(currentPage);
        currentPage = undefined;
      }
      continue;
    }

    if (currentPage === undefined) {
      currentPage = {
        address: start,
        quantity: last - start,
      };
    }

    if (currentPage.address + maxPageSize >= last && optimize && avoidRegister?.avoid !== AvoidType.SPLIT_PAGE) {
      currentPage.quantity = last - currentPage.address;
    } else {
      pages.push(currentPage);
      currentPage = {
        address: start,
        quantity: last - start,
      };
    }
  }

  if (currentPage) {
    pages.push(currentPage);
  }
  return pages;
};

/** Decode Inverter attributes */
const getInverterAttribute = (register: InverterRegister, content: number[]): number | string | null | BigInt => {
  const { dataType, accuracy } = register;
  if (dataType === DataType.ASCII) {
    return cleanString(content.map((v) => String.fromCharCode(v)).join('')) as string;
  }

  if (dataType === DataType.U16) {
    const value = content[0] << 8 | content[1];
    return value * (accuracy || 1);
  }

  /**
   * Integer values can be negative
   */
  if (dataType === DataType.I16) {
    const value = content[0] << 8 | content[1];
    return (value & 0x8000 ? value - 0x10000 : value) * (accuracy || 1);
  }

  if (dataType === DataType.U32) {
    // >>> 0 converts JS signed-32-bit result to unsigned
    const value = (content[0] << 24 | content[1] << 16 | content[2] << 8 | content[3]) >>> 0;
    return value * (accuracy || 1);
  }

  if (dataType === DataType.I32) {
    // JS bitwise ops already return signed 32-bit — no extra conversion needed
    const value = content[0] << 24 | content[1] << 16 | content[2] << 8 | content[3];
    return value * (accuracy || 1);
  }

  if (dataType === DataType.U64) {
    const shifts: number[] = [56, 48, 40, 32, 24, 16, 8, 0];
    const results: string[] = content.map((value, index) => multiplyStringByTwoPower(value.toString(), shifts[index]));
    const total: string = results.reduce(addStrings, '0');
    return BigInt(total);
  }

  if (dataType === DataType.BCD16) {
    return `${content[0].toString(16)}.${content[1].toString(16).padStart(2, '0')}`;
  }

  if (dataType === DataType.BCD_FULL) {
    return content.map((value) => value.toString(16).padStart(2, '0')).join('');
  }

  throw new Error(`Unknown data type: ${dataType}`);
};

/**
 * Get InverterKey - value using a register table
 */
export const getHoldingRegistersValueByTable = (
  startAddress: number,
  quantity: number,
  data: Uint8Array,
  registers: Partial<RegisterTable>,
): { [InverterKey: string]: number | string | null | BigInt } => {
  const decodedRegisters = decodeHoldingRegisters(startAddress, quantity, data);
  const result: { [InverterKey: string]: string | number | null | BigInt } = {};
  for (const register of Object.entries(registers)) {
    const [key, value] = register;
    if (value === undefined) {
      throw new Error(`Register ${key} is not defined`);
    }

    const isAvailable = Array(value.quantity)
      .fill(0)
      .map((_, i) => value.address + i)
      .every((address) => decodedRegisters[address] !== undefined);

    if (isAvailable) {
      const content = Array(value.quantity)
        .fill(0)
        .map((_, i) => getArrayUint8FromHex(decodedRegisters[value.address + i]))
        .flat();

      result[key] = getInverterAttribute(value, content);
    }
  }
  return result;
};

export const printIntAsHex = (value: number): string => value.toString(16).padStart(2, '0');
export const printUint8ArrayAsHex = (value: Uint8Array): string => Array.from(value).map(printIntAsHex).join(' ');

export const getArrayUint8FromHex = (value: number): number[] => new Array(value >> 8, value & 0xff);

export const fromHexStringToUint8Array = (hexString: string): Uint8Array => {
  const result = new Uint8Array(hexString.length / 2);
  for (let i = 0; i < hexString.length; i += 2) {
    result[i / 2] = parseInt(hexString.substring(i, i + 2), 16);
  }
  return result;
};

export const fromHexGetTwoChars = (hex: number): string => hex.toString(16).padStart(2, '0');

export const compareBuffer = (firstBuffer: ArrayBuffer, secondBuffer: ArrayBuffer) => {
    if (firstBuffer.byteLength !== secondBuffer.byteLength) {
      return false;
    }
    const [a1, a2] = [new Int8Array(firstBuffer), new Int8Array(secondBuffer)];
    for (let i = 0 ; i !== firstBuffer.byteLength ; i++) {
        if (a1[i] !== a2[i]) {
          return false;
        }
    }
    return true;
};

export const fromNumberToNBytes = (value: number, bytes: number): Uint8Array => {
  const result = new Uint8Array(bytes);
  for (let i = 0; i < bytes; i++) {
    result[i] = value >> (8 * i) & 0xff;
  }
  return result;
};

export const fromNBytesToNumber = (value: Uint8Array): number => {
  let result = 0;
  for (let i = 0; i < value.length; i++) {
    result += value[i] << (8 * i);
  }
  return result;
};

export const cleanString = (input?: string) => input?.replace(/[\x00-\x1F\x7F-\x9F\u2000-\u200F\u2028-\u202F\u205f\u3000]/g, '');

function addStrings(a: string, b: string): string {
  let i = a.length - 1;
  let j = b.length - 1;
  let carry = 0;
  let result = '';

  while (i >= 0 || j >= 0 || carry) {
      const sum = (i >= 0 ? parseInt(a[i], 10) : 0) + (j >= 0 ? parseInt(b[j], 10) : 0) + carry;
      result = (sum % 10) + result;
      carry = Math.floor(sum / 10);
      i--;
      j--;
  }

  return result;
}

function multiplyStringByTwoPower(str: string, power: number): string {
  for (let i = 0; i < power; i++) {
      str = addStrings(str, str);
  }
  return str;
}

/**
 * Check if a bit is enabled
*/
export const isEnabledByBit = (value: InverterContentType, bit: number) => {
  const config = (value as number).toString(2).padStart(8, '0').split('').reverse().join('');
  return config[bit] === '1';
};

/**
 * Update a bit by position
*/
export const updateBitByPosition = (value: InverterContentType, bit: boolean, position: number): number => {
  return bit
    ? (value as number) | (1 << position)
    : (value as number) & ~(1 << position);
};

/**
 * Get bits values
*/
export const getBitsValue = (value: InverterContentType, start: number, length: number): number => {
  const config = (value as number).toString(2).padStart(8, '0').split('').reverse().join('');
  return parseInt(config.slice(start, start + length).split('').reverse().join(''), 2);
}

/**
 * Update bits values
*/
export const updateBitsValue = (value: InverterContentType, bits: number, start: number, length: number): number => {
  const config = (value as number).toString(2).padStart(8, '0').split('').reverse().join('');
  const newValue = bits.toString(2).padStart(length, '0').split('').reverse().join('');
  const result = config.slice(0, start) + newValue + config.slice(start + length);
  return parseInt(result.split('').reverse().join(''), 2);
}

type DecodedError = {
  id: number;
  features: ErrorFeature[];
};

type DecodedHistoryEvent = {
  id: number;
  feature?: ErrorFeature;
};

const decodeErrorBuilder = (_faults: ErrorCatalog, version: number) => (id: number, value: number): DecodedError => {
  if (id < 1 || id > 60 || value < 0 || value > 0xFFFF) {
    return {
      id,
      features: [{
        description: `Unknown error ${id}/${value}`,
        source: 'Unknown',
        classification: 'Unknown',
      }],
    };
  }

  const errorFeatures = _faults[id];
  if (!errorFeatures) {
    return {
      id,
      features: [{
        description: `Unknown error ${id}/${value}`,
        source: 'Unknown',
        classification: 'Unknown',
      }],
    };
  }

  const features: ErrorFeature[] = [];
  for (let i = 0; i < 16; i++) {
    const bit = 1 << i;
    if ((value & bit) !== 0 && errorFeatures[bit]) {
      features.push(errorFeatures[bit]);
    }
  }

  return {
    id,
    features,
  };
};

/**
 * Error catalog for inverters
 */
export const decodeErrorG3 = decodeErrorBuilder(faultsG3, 3);
export const decodeErrorG2 = decodeErrorBuilder(faultsG2, 2);
export const decodeErrorG1 = decodeErrorBuilder(faultsG1, 1);

export const decodePowerMagicEvent = (id: number): DecodedHistoryEvent => {
  const faultsPowerMagicFlat: Record<number, ErrorFeature> = {};
  Object.values(faultsPowerMagic).forEach((group) => {
    Object.entries(group).forEach(([key, feature]) => {
      faultsPowerMagicFlat[Number(key)] = feature;
    });
  });
  // * Normalize power magic levels to inverter levels
  const normalizeLevel = (level?: number): 1 | 2 | undefined => {
    if (level === 2) return 1;
    if (level === 3) return 2;
    return undefined;
  };

  const errorFeature = faultsPowerMagicFlat[id]
    ? { ...faultsPowerMagicFlat[id] }
    : undefined;
  if (!errorFeature) {
    return {
      id,
      feature: {
        description: `Unknown error ${id}`,
        source: 'Unknown',
        classification: 'Unknown',
      },
    };
  }

  errorFeature.level = normalizeLevel(errorFeature.level);

  return {
    id,
    feature: errorFeature,
  };
}

export const decodeHistoryEventG3 = (id: number, value: number): DecodedHistoryEvent => {
  if (id < 1 || id > 60 || value < 0 || value > 0xFFFF) {
    return {
      id,
      feature: {
        description: `Unknown error ${id}/${value}`,
        source: 'Unknown',
        classification: 'Unknown',
      },
    };
  }

  const errorFeatures = faultsG3[id];
  if (!errorFeatures) {
    throw new Error(`Error features not found for ID ${id}, value=${value} (version=3)`);
  }

  const feature = errorFeatures[1 << value];

  return {
    id,
    feature,
  };
};

export const decodeHistoryEventG2 = (id: number): DecodedHistoryEvent => {
  if (id < 1 || id > 200 ) {
    return {
      id,
      feature: {
        description: `Unknown error ${id}`,
        source: 'Unknown',
        classification: 'Unknown',
      },
    };
  }

  let errorFeatures;
  for (const [_, value] of Object.entries(faultsG2)) {
    if (value) {
      Object.values(value).forEach((_feature) => {
        if (_feature.id === id) {
          errorFeatures = _feature;
        }
      });
    }
  }

  if (!errorFeatures) {
    return {
      id,
      feature: {
        description: `Unknown error ${id}`,
        source: 'Unknown',
        classification: 'Unknown',
      },
    };
  }

  return {
    id,
    feature: errorFeatures,
  };
};

export const decodeHistoryEventG1 = (id: number): DecodedHistoryEvent => {
  if (id < 1 || id > 100 ) {
    return {
      id,
      feature: {
        description: `Unknown error ${id}`,
        source: 'Unknown',
        classification: 'Unknown',
      },
    };
  }

  let errorFeatures;
  for (const [_, value] of Object.entries(faultsG1)) {
    if (value) {
      Object.values(value).forEach((_feature) => {
        if (_feature.id === id) {
          errorFeatures = _feature;
        }
      });
    }
  }

  if (!errorFeatures) {
    return {
      id,
      feature: {
        description: `Unknown error ${id}`,
        source: 'Unknown',
        classification: 'Unknown',
      },
    };
  }

  return {
    id,
    feature: errorFeatures,
  };
};

export const RetrieveRegisters = (content: Partial<InverterData>, registerTables: Array<Partial<RegisterTable> | Partial<InverterDataComposer>>) => {
  return registerTables.reduce((acc, table) => {
    if (content) {
      Object.keys(table).forEach((key) => {
        if (key in content) {
          acc[key] = content[key] as InverterContentType;
        }
      });
    }
    return acc;
  }, {} as Record<keyof InverterData, InverterContentType>);
};

export const BuildRetrieveRegisters = (registerTables: Array<Partial<RegisterTable> | Partial<InverterDataComposer>>) => {
  return (content: Partial<InverterData>) => RetrieveRegisters(content, registerTables);
}

